import React, { useState } from "react";
import { Alert } from "react-bootstrap";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import DynamicFormRenderer from "./DynamicFormRenderer";
import "./FormPreview.css";

const VIEWPORTS = [
  { id: "desktop", label: "Desktop", icon: DesktopWindowsOutlinedIcon },
  { id: "tablet", label: "Tablet", icon: TabletMacOutlinedIcon },
  { id: "mobile", label: "Mobile", icon: PhoneIphoneOutlinedIcon },
];

// Phase 3A. Live, unsaved-edits preview inside the builder — wraps the
// shared DynamicFormRenderer (also used for the standalone "Preview" API
// page). Renders directly from in-memory builder state, not a server
// round-trip, so it reflects edits before they're saved.
//
// The validation block below is the ONE place a blocked publish's exact
// reason is surfaced -- every issue the backend's validatePublishReadiness()
// returns is a { field_key, message, code } triple, listed in full (not
// just a "N issue(s)" count) so a form author never has to guess which
// field or rule actually failed.
export default function FormPreview({ sections, validation }) {
  // Local, presentation-only -- no other consumer needs which viewport an
  // admin is currently previewing at.
  const [viewport, setViewport] = useState("desktop");

  return (
    <div className="form-preview">
      <Alert variant="info" className="form-preview-mode-banner">
        Preview Mode — Responses are not saved.
      </Alert>

      <div className="form-preview-viewport-toggle" role="group" aria-label="Preview viewport size">
        {VIEWPORTS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`form-preview-viewport-btn${viewport === id ? " is-active" : ""}`}
            onClick={() => setViewport(id)}
            aria-pressed={viewport === id}
          >
            <Icon fontSize="small" aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      {validation && (
        <Alert
          variant={validation.is_publishable ? "success" : "danger"}
          className="form-preview-validation-alert"
        >
          <div className="form-preview-validation-heading">
            {validation.is_publishable ? (
              <CheckCircleOutlineIcon fontSize="small" aria-hidden="true" />
            ) : (
              <ErrorOutlineIcon fontSize="small" aria-hidden="true" />
            )}
            <span>
              {validation.is_publishable
                ? "This version is ready to publish."
                : `This version is not ready to publish — ${validation.errors?.length || 0} issue(s) found:`}
            </span>
          </div>
          {!!validation.errors?.length && (
            <ul className="form-preview-validation-list">
              {validation.errors.map((e, i) => (
                <li key={i}>
                  {e.field_key && <span className="form-preview-validation-field">{e.field_key}</span>}
                  <span>{e.message}</span>
                  {e.code && <span className="form-preview-validation-code">{e.code}</span>}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}
      <div className={`form-preview-frame form-preview-frame-${viewport}`}>
        <DynamicFormRenderer sections={sections} mode="preview" />
      </div>
    </div>
  );
}
